import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink } from 'lucide-react';

/**
 * Composant d'avertissement pédagogique réutilisable.
 *
 * Affiche un encart compact rappelant :
 *   - que les contenus sont pédagogiques et non officiels
 *   - que le Code de la route évolue
 *   - qu'en cas de doute, il faut se référer à Légifrance
 *   - lien vers l'avertissement complet (Mentions légales §6bis)
 *
 * Utilisé dans :
 *   - Page Révision (top ou bottom)
 *   - Page Examen ETM (avant lancement de l'épreuve)
 *   - Page Lexique / Programme ETM
 *
 * Props :
 *   - compact (bool)  : version courte 1 ligne (par défaut false = version 3 lignes)
 *   - className       : classes Tailwind additionnelles
 *   - testid          : data-testid pour tests
 */
export function ContentDisclaimer({ compact = false, className = '', testid = 'content-disclaimer' }) {
  if (compact) {
    return (
      <div
        data-testid={testid}
        className={`rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] leading-relaxed text-foreground/75 ${className}`}
      >
        <p className="flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <span>
            <strong>Contenus pédagogiques non officiels.</strong> En cas de doute, consulter{' '}
            <a
              href="https://www.legifrance.gouv.fr/codes/texte_lc/LEGITEXT000006074228/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[hsl(var(--primary))]"
            >
              Légifrance
            </a>
            .{' '}
            <Link
              to="/mentions-legales#mentions-content-disclaimer"
              className="underline hover:text-[hsl(var(--primary))]"
            >
              Détails
            </Link>
          </span>
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid={testid}
      className={`rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 sm:p-4 ${className}`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-bold text-amber-600 dark:text-amber-400 mb-1">
            Outil d'entraînement — contenus pédagogiques non officiels
          </p>
          <p className="text-[11px] sm:text-xs text-foreground/80 leading-relaxed">
            Les questions et explications sont rédigées par des ECSR et enrichies par IA avec contrôle humain.
            <strong> Le Code de la route évolue régulièrement.</strong> Aucune garantie n'est donnée sur
            l'exhaustivité ou l'actualité. En cas de doute, référez-vous au{' '}
            <a
              href="https://www.legifrance.gouv.fr/codes/texte_lc/LEGITEXT000006074228/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold hover:text-[hsl(var(--primary))] inline-flex items-center gap-0.5"
            >
              Code de la route sur Légifrance
              <ExternalLink className="w-2.5 h-2.5" />
            </a>
            {' '}ou à votre moto-école. L'éditeur ne peut être tenu responsable.{' '}
            <Link
              to="/mentions-legales#mentions-content-disclaimer"
              className="underline font-semibold hover:text-[hsl(var(--primary))]"
              data-testid={`${testid}-full-link`}
            >
              Avertissement complet
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

export default ContentDisclaimer;
